import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "../cms/store.js";
import { track } from "../tracking.js";
import { funnel, descBucket } from "../funnel.js";
import { resolvePeople, signRowHtml } from "./trust.mjs";
import { CATEGORIES, KINDS, KIND_LABEL, makeTitle, pickService, zl } from "./landingCopy.mjs";

/*
 * Pierwszy krok zamówienia na stronie docelowej z reklamy: rodzaj usługi z
 * ceną, kategoria i opis sprawy. Bez logowania i bez zakładania konta — dalej
 * przechodzimy do /panel/nowa z przeniesionymi danymi (ten sam mechanizm
 * stanu routera co chorągiewka QuickCaseFlag.jsx i przycisk „Zamów”), a konto
 * użytkownik zakłada dopiero przy składaniu zamówienia (NewCase.jsx).
 *
 * To CELOWO nie jest wyciągnięty fragment NewCase.jsx: formularz zamówienia to
 * strona, na której zbiera się płatności, więc zostaje nietknięty. Tu tylko
 * zbieramy trzy pola i przekazujemy je dalej.
 *
 * Wpisany tekst trzymamy w sessionStorage — powrót strzałką „wstecz” z
 * formularza zamówienia nie może zjadać opisu, który ktoś właśnie napisał.
 */

const DRAFT_KEY = "prawly:lp-draft";

function readDraft(slug) {
  try {
    const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    return d && d.slug === slug ? d : {};
  } catch {
    return {};
  }
}

export default function LandingStart({ landing, services, servicesState }) {
  const nav = useNavigate();
  const shared = useContent("landingShared") || {};
  const team = useContent("team");
  const signRow = useMemo(() => signRowHtml(resolvePeople(team), shared.lawyer?.name), [team, shared.lawyer?.name]);
  const started = useRef(false);
  const formRef = useRef(null);
  const draft = useRef(readDraft(landing.slug)).current;
  const [kind, setKind] = useState(draft.kind || landing.kind || "opinion");
  // Dziedzinę ustawia strona (konfiguracja), a zmienić ją można w kolejnym kroku —
  // jedno pole mniej na pierwszym ekranie, który ma mieścić się w oknie.
  const category = CATEGORIES.includes(landing.category) ? landing.category : CATEGORIES[CATEGORIES.length - 1];
  const [description, setDescription] = useState(draft.description || "");

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ slug: landing.slug, kind, description }));
    } catch {
      /* prywatne okno — formularz działa, tylko bez zapamiętywania */
    }
  }, [landing.slug, kind, description]);

  /* Przejęcie statycznego formularza z prerenderu (patrz scripts/prerender-landings.mjs): tekst wpisany, zanim aplikacja się
     załadowała, wraca tu przez szkic (sessionStorage) — liczymy go jako rozpoczęcie opisu; „Dalej” kliknięte w tym czasie
     było wstrzymane (window.__lpSubmit), więc kończymy je teraz, już po zapisaniu źródła wejścia (setTimeout). */
  useEffect(() => {
    if (window.__lpTyped && !started.current && description.trim()) {
      started.current = true;
      track("lp_start", { landing: landing.slug, kind });
      funnel("lp_typed", { kind });
    }
    if (!window.__lpSubmit) return undefined;
    window.__lpSubmit = 0;
    const t = setTimeout(() => formRef.current?.requestSubmit(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = KINDS
    .map((k) => [k, pickService(services, k)])
    .filter(([, s]) => servicesState !== "ready" || s);
  const current = pickService(services, kind);
  const len = description.trim().length;

  // Opis nie jest tu wymagany: kto chce, przechodzi dalej od razu, a opisze sprawę (lub jej temat) na stronie zamówienia.
  const next = (e) => {
    e.preventDefault();
    funnel("lp_next", { kind, desc: descBucket(len) });
    nav(current ? "/panel/nowa" : `/panel/nowa?kind=${kind}`, {
      state: {
        serviceId: current?.id,
        category,
        title: makeTitle(description),
        description: description.trim(),
      },
    });
  };

  return (
    <form className="lp-card" onSubmit={next} noValidate ref={formRef}>
      <h2 className="lp-card-title">Opisz swoją sprawę</h2>

      <fieldset className="lp-kinds">
        <legend className="lp-label">Czego potrzebujesz?</legend>
        {available.map(([k, s]) => (
          <label key={k} className={`lp-kind${kind === k ? " on" : ""}`}>
            <input type="radio" name="lp-kind" value={k} checked={kind === k} onChange={() => setKind(k)} />
            <span className="lp-kind-name">{KIND_LABEL[k]}</span>
            <span className="lp-kind-meta">
              {s ? `${zl(s.price_cents)} · do ${s.sla_hours} h rob.` : "…"}
            </span>
          </label>
        ))}
      </fieldset>

      <label className="lp-label" htmlFor="lp-desc">
        Opis sprawy
      </label>
      <textarea
        id="lp-desc"
        className="lp-input lp-area"
        rows={4}
        value={description}
        placeholder={landing.placeholder}
        onChange={(e) => {
          setDescription(e.target.value);
          // rozpoczęcie opisu sprawy — raz na wizytę na danej stronie
          if (!started.current && e.target.value.trim()) {
            started.current = true;
            track("lp_start", { landing: landing.slug, kind });
            funnel("lp_typed", { kind });
          }
        }}
        aria-describedby="lp-desc-hint"
      />
      <p id="lp-desc-hint" className="lp-hint">
        Im więcej faktów i dat, tym trafniejsza odpowiedź.
      </p>
      {shared.confidentiality && <p className="lp-conf">{shared.confidentiality}</p>}

      <button type="submit" className="btn wax lp-submit">
        Dalej — dane i płatność
      </button>
      {shared.risk && <p className="lp-risk">{shared.risk}</p>}
      <p className="lp-note">
        {current ? `Do zapłaty: ${zl(current.price_cents)} · do ${current.sla_hours} godz. roboczych. ` : ""}
        Konto założysz przy składaniu zamówienia.
      </p>
      {signRow && <div dangerouslySetInnerHTML={{ __html: signRow }} />}
    </form>
  );
}
